import { ArrowUp } from 'iconsax-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setIsPrefillForm,
  setSocialMedia,
  setSubjectInfo,
} from '@/store/api/RunReport/RunReport.slice'
import type {
  findSubjectItem,
  findSubjectLocation,
} from '@/store/api/RunReport/RunReport.types'
import { convertToBase64 } from '@/utils/binary'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'
import { proxiedImageUrl } from '@/utils/proxiedImageUrl'

const MISSING_FIELD_FALLBACK = '—'

const formatLocation = (location?: findSubjectLocation): string => {
  if (!location) return MISSING_FIELD_FALLBACK
  return (
    location.city || location.full || location.country || MISSING_FIELD_FALLBACK
  )
}

interface SearchResultRowProps {
  data: findSubjectItem
}

export const SearchResultRow = ({ data }: SearchResultRowProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const fullName = `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim()
  const initials = useMemo(() => {
    if (!data.firstName) return ''
    const first = data.firstName?.[0]?.toUpperCase() ?? ''
    const second = data.lastName?.[0]?.toUpperCase() ?? ''
    return `${first}${second}`
  }, [data.firstName, data.lastName])

  const role = data.headline || MISSING_FIELD_FALLBACK
  const location = formatLocation(data.location)

  const handleClick = async () => {
    // Through the proxy as well: this fetch is stopped by the same shields, and
    // when it fails the report is created without the subject's photo.
    const image = await convertToBase64(
      proxiedImageUrl(data.profilePicture) ?? null,
    )
    const subjectInfo = {
      ...slice.subjectInfo,
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      alias: data.alias ?? '',
      college: data.college ?? '',
      employerName: data.company ?? '',
      countryLongName: data.location?.country ?? '',
      cityLongName: data.location?.city ?? '',
      image,
    }
    dispatch(setIsPrefillForm(true))
    dispatch(setSubjectInfo(subjectInfo))
    dispatch(setSocialMedia({ linkedIn: data.profileURL ?? '' }))
    navigate(AppRoutes.runReportSubjectForm)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full shrink-0 items-center gap-3 border-b border-neutralColor5 px-3 py-3 text-left transition-colors hover:bg-baseColorHighlight md:gap-4 md:py-1.5 min-h-[44px]"
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage
          src={proxiedImageUrl(data.profilePicture)}
          alt={fullName}
        />
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm md:flex-row md:items-center md:gap-4">
        <span className="min-w-0 flex-1 truncate text-neutralColor">
          {fullName}
        </span>
        <span className="min-w-0 flex-[2] truncate text-neutralColor60 max-md:hidden">
          {role}
        </span>
        <span className="min-w-0 flex-1 truncate text-neutralColor60 max-md:text-xs">
          {location}
        </span>
      </div>

      <ArrowUp
        size="16"
        color={weCheckPalette.neutralColor60}
        className="rotate-45"
      />
    </button>
  )
}
